import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import axios from 'axios';
import { ArrowPathIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import MembersSection from './organization/MembersSection';
import ContextSection from './organization/ContextSection';
import UsageSection from './organization/UsageSection';
import PreferencesSection from './organization/PreferencesSection';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const OrganizationSection = ({ user, activeTab = 'members' }) => {
  const { darkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [userRole, setUserRole] = useState('member');
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Toast style based on dark mode
  const toastStyle = {
    style: {
      background: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#000',
      border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
    },
    success: {
      style: {
        background: darkMode ? '#0c4a2d' : '#ecfdf5',
        color: darkMode ? '#fff' : '#065f46',
        border: `1px solid ${darkMode ? '#065f46' : '#d1fae5'}`,
      },
    },
    error: {
      style: {
        background: darkMode ? '#4c1d24' : '#fef2f2',
        color: darkMode ? '#fff' : '#b91c1c',
        border: `1px solid ${darkMode ? '#b91c1c' : '#fee2e2'}`,
      },
    },
  };

  // Load organization details on component mount
  useEffect(() => {
    fetchOrganizationInfo();
  }, []);

  const fetchOrganizationInfo = async () => {
    try {
      setIsLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found');
        setIsLoading(false);
        return;
      }
      
      // Get organization basic info
      const orgResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organization`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // If user doesn't belong to an org, set organization to null and stop loading
      if (!orgResponse.data.hasOrganization) {
        setOrganization(null);
        setMembers([]);
        setUserRole(null);
        setIsLoading(false);
        return;
      }
      
      // If user belongs to an org, get members
      const membersResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/organization/members`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Set state with fetched data
      setOrganization(membersResponse.data.organization);
      setMembers(membersResponse.data.members);
      setUserRole(membersResponse.data.userRole);
      
    } catch (error) {
      console.error('Error fetching organization info:', error);
      
      if (error.response?.status === 404) {
        // This is normal for users not in an org
        setOrganization(null);
        setMembers([]);
        setUserRole(null);
      } else {
        toast.error('Failed to load organization information', toastStyle.error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render the active section based on activeTab
  const renderActiveSection = () => {
    const commonProps = {
      user,
      organization,
      members,
      setMembers,
      userRole,
      toastStyle,
      darkMode
    };

    switch (activeTab) {
      case 'members':
        return <MembersSection {...commonProps} />;
      case 'context':
        return <ContextSection {...commonProps} />;
      case 'usage':
        return <UsageSection {...commonProps} />;
      case 'preferences':
        return <PreferencesSection {...commonProps} />;
      default:
        return <MembersSection {...commonProps} />;
    }
  };

  // Info Modal Component
  const InfoModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="account-modal bg-white dark:bg-[#111111] rounded-2xl border border-[#dbdbdb] dark:border-[#444444] max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <InformationCircleIcon className="h-6 w-6 text-black dark:text-white mr-2" />
              <h3 className="text-lg font-medium text-black dark:text-white">About Organizations</h3>
            </div>
            <button
              onClick={() => setShowInfoModal(false)}
              className="account-modal-btn p-1 text-[#a3a3a3] hover:text-[#525252] dark:hover:text-[#999999] rounded-lg"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <div className="text-sm text-[#737373] dark:text-[#999999]">
            <p className="mb-3">Organizations allow you to:</p>
            <ul className="list-disc ml-5 space-y-2">
              <li>Group team members under a single company identity</li>
              <li>Customize your experience with branded assets</li>
              <li>Provide your team access through a custom branded URL</li>
              <li>Control who has access to your organization's resources</li>
              <li>Manage permissions with different member roles</li>
              <li>Upload and manage documents for your organization</li>
            </ul>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setShowInfoModal(false)}
              className="account-modal-btn px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-[#262626] dark:hover:bg-[#e0e0e0] text-sm font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
      {/* Loading indicator */}
      {isLoading && (
        <div className="mt-6 space-y-6">
          <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
            {/* Organization card skeleton */}
            <div className="p-4 bg-gray-50 dark:bg-[#222] rounded-xl border border-gray-200 dark:border-[#2a2a2a]">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <Skeleton height={20} width={150} className="mb-2" />
                  <Skeleton height={16} width={250} className="mb-4" />
                  <div className="flex items-center space-x-2">
                    <Skeleton height={24} width={80} />
                    <Skeleton height={24} width={60} />
                  </div>
                </div>
                <Skeleton height={40} width={40} />
              </div>
            </div>
            
            {/* Members/content skeleton */}
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-[#222] rounded-lg border border-gray-200 dark:border-[#2a2a2a]">
                  <Skeleton height={40} width={40} />
                  <div className="flex-1">
                    <Skeleton height={16} width={120} className="mb-1" />
                    <Skeleton height={14} width={180} />
                  </div>
                  <Skeleton height={32} width={80} />
                </div>
              ))}
            </div>
          </SkeletonTheme>
        </div>
      )}
      
      {/* Organization content */}
      {!isLoading && organization && (
        <section className="space-y-0">
          {renderActiveSection()}
        </section>
      )}
      
      {/* No organization message */}
      {!isLoading && !organization && (
        <section className="pb-5 border-b border-[#ededed] dark:border-[#2a2a2a]">
          <p className="text-sm text-[#737373] dark:text-[#999999]">
            You are not part of an organization. Contact your organization administrator to get invited.
          </p>
        </section>
      )}
      
      {/* About link - matches Settings tab footer pattern */}
      <div className="pt-5">
        <button
          type="button"
          onClick={() => setShowInfoModal(true)}
          className="account-modal-btn text-sm text-[#737373] dark:text-[#999999] hover:text-[#404040] dark:hover:text-[#cccccc] underline px-2 py-1 rounded-lg"
        >
          About organizations
        </button>
      </div>
      
      {/* Info Modal */}
      {showInfoModal && <InfoModal />}
    </div>
  );
};

export default OrganizationSection; 